import { Prisma } from "@prisma/client";

export enum CommercialisationTaskStatus {
  ACTIVE = "active",
  CLOTURED = "clotured",
  ARCHIVED = "archived",
}

export const CommercialisationTaskStatusValues = [
  "active",
  "clotured",
  "archived",
] as const;

export enum CommercialisationTaskTargetType {
  EVENT = "event",
  ORGANISER = "organiser",
  OTHER = "other",
}

export const CommercialisationTaskTargetTypeValues = [
  "event",
  "organiser",
  "other",
] as const;

export enum CommercialisationTemperatureType {
  HOT = "hot",
  LUKEWARM = "lukewarm",
  COLD = "cold",
}

export const CommercialisationTaskTemperatureTypeValues = [
  "hot",
  "lukewarm",
  "cold",
] as const;

export enum CommercialisationMeetingType {
  PHONE = "phone",
  VISO = "visio",
}

export const CommercialisationMeetingTypeValues = ["phone", "visio"] as const;

export const CommercialisationMeetingValues =
  Prisma.validator<Prisma.CommercialisationMeetingDefaultArgs>()({
    select: {
      id: true,
      isCancelled: true,
      date: true,
      beginningHour: true,
      endHour: true,
      meetingType: true,
      target: {
        select: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          picture: true,
        },
      },
    },
  });

export type CommercialisationMeeting =
  Prisma.CommercialisationMeetingGetPayload<
    typeof CommercialisationMeetingValues
  >;

export const CommercialisationInterlocutorValues =
  Prisma.validator<Prisma.CommercialisationInterlocutorDefaultArgs>()({
    select: {
      id: true,
      name: true,
      email: true,
      phone: true,
      isArchived: true,
      function: true,
      adoptedSolution: true,
      text: true,
      gender: true,
    },
  });

export type CommercialisationInterlocutor =
  Prisma.CommercialisationInterlocutorGetPayload<
    typeof CommercialisationInterlocutorValues
  >;

export const CommercialisationOrganiserEventValues =
  Prisma.validator<Prisma.CommercialisationOrganiserEventsDefaultArgs>()({
    select: {
      id: true,
      name: true,
      startDate: true,
      endDate: true,
      exhibitorCount: true,
      visitorCount: true,
      animationCount: true,
      isArchived: true,
      ticketingAmount: true,
      adoptedSolution: true,
    },
  });

export type CommercialisationOrganiserEvent =
  Prisma.CommercialisationOrganiserEventsGetPayload<
    typeof CommercialisationOrganiserEventValues
  >;

export const CommercialisationTaskCommentValues =
  Prisma.validator<Prisma.CommercialisationTaskCommentDefaultArgs>()({
    select: {
      id: true,
      taskId: true,
      text: true,
      createdAt: true,
      author: {
        select: { id: true, firstName: true, lastName: true, picture: true },
      },
    },
  });

export type CommercialisationTaskComment =
  Prisma.CommercialisationTaskCommentGetPayload<
    typeof CommercialisationTaskCommentValues
  >;

export const CommercialisationTaskValues =
  Prisma.validator<Prisma.CommercialisationTaskDefaultArgs>()({
    select: {
      id: true,
      status: true,
      date: true,
      title: true,
      targetType: true,
      temperature: true,
      targetProvenance: true,
      otherName: true,
      organiserEmail: true,
      organiserPhone: true,
      animationCount: true,
      exhibitorCount: true,
      ticketingAmount: true,
      visitorCount: true,
      event: {
        include: {
          dates: {
            include: {
              schedules: {
                include: {
                  timetables: true,
                },
              },
              exceptionalClosingDates: true,
            },
          },
          organiser: {
            include: {
              admin: true,
            },
          },
          address: true,
          socials: true,
          eventStructures: true,
        },
      },
      assigneeId: true,
      assignee: {
        select: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          picture: true,
        },
      },
      organiser: {
        select: {
          id: true,
          admin: {
            select: {
              id: true,
              firstName: true,
              lastName: true,
              email: true,
              phoneNumber: true,
              address: true,
              city: true,
              zipCode: true,
              picture: true,
            },
          },
          structures: {
            select: {
              structure: {
                select: {
                  eventStructures: true,
                },
              },
            },
          },
        },
      },
      comments: {
        orderBy: { createdAt: "asc" },
        select: CommercialisationTaskCommentValues.select,
      },
      meetings: {
        select: CommercialisationMeetingValues.select,
        orderBy: {
          updatedAt: "desc",
        },
      },
      interlocutors: {
        select: CommercialisationInterlocutorValues.select,
        orderBy: { name: "asc" },
      },
      organiserEvents: {
        select: CommercialisationOrganiserEventValues.select,
        orderBy: { startDate: "asc" },
      },
      mails: {
        orderBy: { updatedAt: "desc" },
      },
    },
  });

export type CommercialisationTask = Prisma.CommercialisationTaskGetPayload<
  typeof CommercialisationTaskValues
>;
